import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { fetchCurrentUser, roleHomePath } from "../lib/auth";

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const errorCode = searchParams.get("error");

  const errorMessage =
    errorCode === "invalid_credentials"
      ? "Wrong email, phone number, or password"
      : errorCode === "missing_credentials"
        ? "Enter both your username and password"
        : errorCode === "local_db_not_initialized"
          ? "Local D1 is not initialized yet. Import the legacy data first."
          : errorCode === "auth_failed"
            ? "Authentication failed due to a server error."
            : null;

  useEffect(() => {
    if (errorCode) {
      return;
    }

    let ignore = false;

    async function checkExistingSession() {
      try {
        const current = await fetchCurrentUser();
        if (!ignore && current.authenticated && current.user) {
          navigate(roleHomePath(current.user.role), { replace: true });
        }
      } catch {
        // Ignore unauthenticated state.
      }
    }

    void checkExistingSession();

    return () => {
      ignore = true;
    };
  }, [errorCode, navigate]);

  return (
    <section className="section-shell flex min-h-screen items-center py-10">
      <div className="mx-auto grid w-full max-w-6xl gap-8 lg:grid-cols-[minmax(0,1fr)_500px]">
        <div className="hidden rounded-[40px] border border-black/8 bg-[linear-gradient(135deg,#071526,#0f2f56_55%,#0ea5b7)] p-10 text-white shadow-[0_28px_80px_rgba(8,23,44,0.18)] lg:flex lg:flex-col lg:justify-between">
          <div>
            <Link className="inline-flex items-center gap-3" to="/">
              <img alt="Novatech Logo" className="h-12 w-12 rounded-2xl object-contain" src="/logo/logo.jpg" />
              <div>
                <p className="font-display text-xl font-semibold tracking-tight">Novatech</p>
                <p className="text-sm text-slate-200">Innovate, Enhance and Sustain</p>
              </div>
            </Link>
            <h1 className="mt-10 max-w-md font-display text-5xl font-semibold tracking-tight">
              Access the Novatech commerce and operations workspace.
            </h1>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-[24px] border border-white/12 bg-white/10 p-4 backdrop-blur-md">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-100">Phone</p>
              <p className="mt-2 text-sm font-semibold">+254 795 952 128</p>
            </div>
            <div className="rounded-[24px] border border-white/12 bg-white/10 p-4 backdrop-blur-md">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-100">Email</p>
              <p className="mt-2 text-sm font-semibold">info@novatch.co.ke</p>
            </div>
          </div>
        </div>

        <div className="glass-panel rounded-[36px] p-8 sm:p-10">
          <Link className="inline-flex items-center gap-3 lg:hidden" to="/">
            <img alt="Novatech Logo" className="h-11 w-11 rounded-2xl object-contain" src="/logo/logo.jpg" />
            <div>
              <p className="font-display text-lg font-semibold tracking-tight text-nv-ink">Novatech</p>
              <p className="text-sm text-nv-slate">Innovate, Enhance and Sustain</p>
            </div>
          </Link>

          <div className="mt-8 lg:mt-0">
            <p className="text-xs font-semibold uppercase tracking-[0.26em] text-nv-electric">Sign in</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-nv-ink sm:text-4xl">Welcome back</h2>
            <p className="mt-4 text-sm leading-7 text-nv-slate">
              Use the same email or phone number and password from the current Novatech system.
            </p>
          </div>

          <form action="/api/auth/login/form" className="mt-8 space-y-5" method="post">
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Email or phone</label>
              <input
                className="field-control px-4 py-3.5 text-sm"
                name="identifier"
                placeholder="Email or phone"
                required
              />
            </div>
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Password</label>
              <input
                className="field-control px-4 py-3.5 text-sm"
                name="password"
                placeholder="Password"
                required
                type="password"
              />
            </div>
            {errorMessage ? <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">{errorMessage}</p> : null}
            <button className="brand-button w-full rounded-2xl px-5 py-3.5 text-sm font-semibold text-white" type="submit">
              Continue to workspace
            </button>
          </form>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 text-sm text-nv-slate">
            <Link className="font-semibold text-nv-electric" to="/">
              Back to site
            </Link>
            <a className="font-semibold text-nv-electric" href="mailto:info@novatch.co.ke">
              info@novatch.co.ke
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
